import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ActivityIndicator,
  type StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {
  playReadingAudio,
  type ReadingAudioHandle,
  type ReadingAudioProgress,
  subscribeReadingPreempted,
} from '#/lib/flashbookAudio'
import {inkOn} from '#/components/flashbooks/FlashBookPlayTheme'
import {IS_WEB} from '#/env'
import {type FlashBookDesign} from '#/themes-flashbook/design'

/** The clip to play: the creator's own recording, or the file they uploaded. */
type Source = {url: string}

/**
 * What a press on the progress track actually hands the handler.
 *
 * Wider than React Native's own press event on purpose: the same handler serves
 * the native responder and the web DOM event, and only the parts it needs are
 * declared.
 */
type TrackPressEvent = {
  nativeEvent?: {locationX?: number; pageX?: number}
  currentTarget?: unknown
}

type Props = {
  /** The recorded reading clip. */
  source: Source
  accent: string
  /** Muted ink for the clock and the trailing hint. */
  mutedColor?: string
  /** The book theme's design — decides the player's shape and wording. */
  design?: FlashBookDesign
  /** Overrides the translated label (the creator uses its own wording). */
  label?: string
  /** Fires when the clip finishes (or fails), so the page can mark it read. */
  onEnded?: () => void
  /** Denser layout for the creator's block card. */
  dense?: boolean
  style?: StyleProp<ViewStyle>
}

const IDLE: ReadingAudioProgress = {position: 0, duration: 0}

/** `m:ss` — a clock the learner can trust while scrubbing. */
function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.round(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * The book's read-aloud player.
 *
 * Replaces a bare "Lecture" button with an actual transport: a play/pause
 * control, a progress measure you can scrub, and a clock. It matters because
 * reading aloud is a *time-based* action — a learner needs to see how far in
 * they are, jump back to a sentence they missed, and know how much is left.
 *
 * The player keeps the theme's personality: `plain` prints on the paper with a
 * hairline measure (storybook), `pill` and `badge` give it a compact bordered
 * home on focused themes.
 *
 * It plays only what the creator put there. There is no synthesised voice
 * underneath a passage nobody narrated — so a transport is drawn only when a
 * clip exists (see `offersReadAloud` in `#/lib/spokenKinds`), and every clip it
 * can be handed is a recording, which has a length: the measure is always
 * scrubbable and the clock is always true.
 */
export function FlashBookReadAloud({
  source,
  accent,
  mutedColor,
  design,
  label,
  onEnded,
  dense,
  style,
}: Props) {
  const {t: l} = useLingui()
  const [status, setStatus] = useState<'idle' | 'playing' | 'loading'>('idle')
  const [finished, setFinished] = useState(false)
  /**
   * The clip would not play.
   *
   * A state of its own, because "nothing happened" is the one thing this control
   * must never do: a take whose bytes the device cannot open used to reset to
   * idle, which reads as a play button that is ignoring the press — the exact
   * report a creator makes ("I can't preview the record"). Saying so costs one
   * line and turns a mystery into a fact they can act on.
   */
  const [failed, setFailed] = useState(false)
  const [progress, setProgress] = useState<ReadingAudioProgress>(IDLE)
  /** Duration read from the media metadata before playing, so the clock is true. */
  const [knownDuration, setKnownDuration] = useState(0)
  const handleRef = useRef<ReadingAudioHandle | null>(null)
  const [trackW, setTrackW] = useState(0)

  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded

  const styleKind = design?.audio.style ?? 'pill'
  const idleLabel =
    label ??
    (design?.audio.label === 'listen'
      ? l`Listen`
      : design?.audio.label === 'play-reading'
        ? l`Play reading`
        : l`Read to me`)

  // Stop on unmount and whenever the source changes, so two players on one page
  // can never talk over each other.
  useEffect(() => {
    return () => {
      handleRef.current?.stop()
      handleRef.current = null
    }
  }, [])

  // The engine only ever plays one voice, so when another player starts this one
  // has already been silenced — drop back to idle instead of leaving a progress
  // bar running for audio nobody can hear.
  useEffect(
    () =>
      subscribeReadingPreempted(() => {
        const current = handleRef.current
        if (current?.isPlaying()) return
        handleRef.current = null
        setStatus('idle')
        setProgress(IDLE)
      }),
    [],
  )

  useEffect(() => {
    setProgress(IDLE)
    setFinished(false)
    setFailed(false)
    setStatus('idle')
    setKnownDuration(0)
  }, [source.url])

  // Read the clip's length before it is played, so the transport can show
  // "0:00 / 0:42" instead of a bar with no scale. Native reports the duration
  // as soon as playback starts instead.
  useEffect(() => {
    if (!IS_WEB) return
    let cancelled = false
    try {
      const probe = new Audio(source.url)
      probe.preload = 'metadata'
      probe.onloadedmetadata = () => {
        if (!cancelled && Number.isFinite(probe.duration)) {
          setKnownDuration(probe.duration)
        }
      }
    } catch {
      // Metadata is a nicety; the player still works without it.
    }
    return () => {
      cancelled = true
    }
  }, [source.url])

  const stop = useCallback(() => {
    handleRef.current?.stop()
    handleRef.current = null
    setStatus('idle')
  }, [])

  const play = useCallback(() => {
    setFinished(false)
    setFailed(false)
    setStatus('loading')
    const done = () => {
      setProgress(IDLE)
      setStatus('idle')
      setFinished(true)
      onEndedRef.current?.()
    }
    handleRef.current = playReadingAudio(source.url, {
      onEnded: done,
      // Heard, or could not be heard — the page counts either as read, so the
      // reader is never stuck, and the control says which it was.
      onError: () => {
        setProgress(IDLE)
        setStatus('idle')
        setFailed(true)
        onEndedRef.current?.()
      },
    })
    setStatus('playing')
  }, [source.url])

  const toggle = useCallback(() => {
    if (status === 'playing' || status === 'loading') {
      stop()
      return
    }
    play()
  }, [play, status, stop])

  // The transport is the only thing that knows the position, and the engine
  // only reports it when asked — so poll it while a clip is running.
  useEffect(() => {
    if (status !== 'playing') return
    const id = setInterval(() => {
      const h = handleRef.current
      if (!h) return
      setProgress(h.getProgress())
    }, 160)
    return () => clearInterval(id)
  }, [status])

  const duration = progress.duration > 0 ? progress.duration : knownDuration
  const fraction =
    duration > 0 ? Math.max(0, Math.min(1, progress.position / duration)) : 0

  const seekTo = useCallback(
    (f: number) => {
      const h = handleRef.current
      if (!h) return
      h.seek(f)
      setProgress(prev => ({...prev, position: f * (duration || 0)}))
    },
    [duration],
  )

  const onTrackPress = useCallback(
    (e: TrackPressEvent) => {
      if (trackW <= 0) return
      const ne = e?.nativeEvent
      let x: number | undefined =
        typeof ne?.locationX === 'number' ? ne.locationX : undefined
      // Web: `locationX` is relative to the *pressable* target, which is not
      // always the track (a tap that lands on the thumb bubbles from there), so
      // fall back to the pointer position against the track's own rect.
      if (x === undefined && typeof ne?.pageX === 'number') {
        const node = e?.currentTarget as
          | {getBoundingClientRect?: () => {left: number}}
          | null
          | undefined
        const rect = node?.getBoundingClientRect?.()
        if (rect) x = ne.pageX - rect.left
      }
      if (x === undefined) return
      seekTo(Math.max(0, Math.min(1, x / trackW)))
    },
    [seekTo, trackW],
  )

  const reading = status === 'playing' || status === 'loading'

  const icon =
    status === 'loading'
      ? null
      : reading
        ? 'pause'
        : finished
          ? 'rotate-ccw'
          : 'play'

  const label2 = reading
    ? l`Pause`
    : failed
      ? l`Could not play this take`
      : idleLabel

  // ── Theme-shaped container ────────────────────────────────────────────────
  /**
   * The player is a control *printed on the page*, so on ruled paper it answers
   * to the ruling like everything else there: its shell is a whole number of
   * rules tall. Otherwise a 54px pill on a 26px rule leaves the caption, the
   * next exercise and everything under them floating between two lines — and a
   * transport that visibly ignores the paper it is sitting on is the single
   * most app-like thing on the page.
   */
  const rule = design
    ? design.block.mode === 'ruled'
      ? (design.page.textureStep ?? design.type.body.lineHeight)
      : 0
    : 0

  const shell = useMemo<ViewStyle>(() => {
    const base = ((): ViewStyle => {
      switch (styleKind) {
        case 'plain':
          return {borderWidth: 0, borderRadius: 0, padding: 0}
        case 'badge':
          return {
            borderWidth: 1,
            borderRadius: 6,
            paddingHorizontal: dense ? 8 : 10,
            paddingVertical: dense ? 6 : 8,
            borderColor: accent + '55',
            backgroundColor: accent + '14',
          }
        case 'pill':
        default:
          return {
            borderWidth: 1,
            borderRadius: 16,
            paddingHorizontal: dense ? 10 : 12,
            paddingVertical: dense ? 7 : 9,
            borderColor: accent + '44',
            backgroundColor: accent + '12',
          }
      }
    })()
    if (rule <= 0 || styleKind === 'plain') return base
    // Two rules tall, with the vertical padding given up to the height: the
    // transport's own control is centred in the shell instead.
    return {
      ...base,
      paddingVertical: 0,
      height: rule * 2,
      alignItems: 'center',
    }
  }, [accent, dense, styleKind, rule])

  const buttonSize = dense ? 30 : 34
  const trackHeight = styleKind === 'plain' ? 3 : 4

  const secondsElapsed = progress.position

  return (
    <View
      style={[
        styles.wrap,
        shell,
        style,
        styleKind === 'plain' && {gap: dense ? 8 : 10},
      ]}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={
          reading
            ? l`Pause reading`
            : failed
              ? l`Try the reading again`
              : idleLabel
        }
        accessibilityHint={l`Plays this page aloud so you can read along`}
        onPress={toggle}
        style={[
          styles.playBtn,
          {
            width: buttonSize,
            height: buttonSize,
            borderRadius: styleKind === 'badge' ? 6 : buttonSize / 2,
            backgroundColor: accent,
          },
        ]}>
        {icon ? (
          <Feather name={icon} size={dense ? 14 : 15} color={inkOn(accent)} />
        ) : (
          <ActivityIndicator size="small" color={inkOn(accent)} />
        )}
      </TouchableOpacity>

      <View style={styles.middle}>
        <View style={styles.topRow}>
          <Text
            testID={failed ? 'flashbook-audio-failed' : undefined}
            style={[
              styles.label,
              {color: failed ? (mutedColor ?? accent) : accent},
              dense && styles.labelDense,
            ]}
            numberOfLines={1}>
            {label2}
          </Text>
          {duration > 0 && (
            <Text style={[styles.clock, {color: mutedColor ?? accent + 'aa'}]}>
              {`${formatClock(secondsElapsed)} / ${formatClock(duration)}`}
            </Text>
          )}
        </View>

        {/* The measure. Every clip is a recording, so it has a length and the
            bar is always scrubbable: a tap anywhere on it is a jump. */}
        <TouchableOpacity
          accessibilityRole="adjustable"
          accessibilityLabel={l`Reading progress`}
          accessibilityValue={{
            min: 0,
            max: 100,
            now: Math.round(fraction * 100),
          }}
          accessibilityHint={l`Tap to jump within the recording`}
          activeOpacity={0.7}
          onPress={onTrackPress}
          onLayout={e => setTrackW(e.nativeEvent.layout.width)}
          style={styles.trackTouch}>
          <View
            style={[
              styles.track,
              {
                height: trackHeight,
                backgroundColor: (mutedColor ?? accent) + '33',
              },
            ]}>
            <View
              style={[
                styles.fill,
                {
                  width: `${Math.round(fraction * 100)}%`,
                  backgroundColor: accent,
                  height: trackHeight,
                },
              ]}
            />
          </View>
        </TouchableOpacity>
      </View>

      {reading && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Stop reading`}
          accessibilityHint={l`Stops the reading audio`}
          onPress={stop}
          style={styles.stopBtn}>
          <Feather
            name="square"
            size={dense ? 12 : 13}
            color={mutedColor ?? accent}
          />
        </TouchableOpacity>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  playBtn: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  middle: {flex: 1, gap: 5},
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  label: {fontSize: 12.5, fontWeight: '700', letterSpacing: 0.2},
  labelDense: {fontSize: 12},
  clock: {
    fontSize: 11,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  trackTouch: {justifyContent: 'center'},
  track: {borderRadius: 99, overflow: 'hidden', width: '100%'},
  fill: {borderRadius: 99},
  stopBtn: {padding: 4},
})
